/**
 * `LakiruutuVideoCard` — one `/lakiruutu` video entry (029 US2 / T024).
 *
 * Extracted from `LakiruutuRoute` so the route stays under the 400-line
 * architecture ceiling while this file owns the per-entry presentation to
 * proto parity (the proto's `Lakiruutu.tsx`):
 *
 *   - a 96x80 media affordance whose form depends on the videoUrl mode (the
 *     media re-point made `videoUrl` DUAL-MODE):
 *       - SELF-HOSTED (`/media/x.mp4`, same-origin video asset) → an INLINE
 *         `<video controls>` played from our own origin (primary content: not
 *         muted, not aria-hidden, accessible name from the entry title). This
 *         REPLACES the link-out button — a `<video controls>` is itself
 *         interactive and must not nest inside a `<button>`.
 *       - EXTERNAL (`https://…`) → today's LINK-OUT (research D9, human R2
 *         decision): a `<button>` whose `onClick` opens `video.videoUrl` in a new
 *         tab via `window.open(url, '_blank', 'noopener,noreferrer')` — no in-app
 *         iframe modal, so no edge CSP `frame-src` widening. The button is
 *         `disabled` when the entry has no SAFE http(s) `videoUrl` (`isSafeHttpUrl`,
 *         review finding: extends the proto's `disabled={!videoUrl}` empty-string
 *         contract to also reject a javascript:/data: scheme before it reaches
 *         `window.open`). The `noopener,noreferrer` features give the same
 *         protection an anchor's `rel` would (no `window.opener`, no Referer).
 *     The thumbnail is IMAGE-only after the re-point: it is the poster `<img>`
 *     for the external button, or the `poster` attribute of the inline video.
 *   - the entry title as an `<h3>`.
 *   - a collapsible "Katso lisää tietoja" disclosure whose body is the authored
 *     HTML `description` rendered through `SanitizedHtml` (FR-049 / TA25 — never
 *     `dangerouslySetInnerHTML`). The body stays MOUNTED when collapsed (hidden
 *     via the `hidden` attribute) so it is always sanitised in place. Since
 *     024 B1 the read-shape `description` is projected BODY-FIRST (the authored
 *     rich `body` wins; the legacy plain `description` is the fallback), so
 *     rendering `video.description` here IS "body || description" — no per-field
 *     branching is needed on the member side (the projection did the merge).
 *
 * (024 D-auto: the video read shape no longer carries a `category` field —
 * it was read-but-never-rendered and has been removed.)
 */

import { type JSX, useId, useState } from 'react';
import { isVideoUrl } from '../../components/cms/isVideoUrl.js';
import { isSameOriginAssetPath } from '../../components/cms/mediaAssetUrl.js';
import type { MessageKeys } from '../../i18n/messages/index.js';
import { ChevronDownIcon } from '../../shell/icons.js';
import type { VideoListing } from '../ContentReaderPort.js';
import { SanitizedHtml } from '../components/SanitizedHtml.js';
import { isSafeHttpUrl } from '../safeUrl.js';

const cardClasses = 'relative rounded-lg border border-border bg-card p-4';
const topRowClasses = 'mb-3 flex items-center gap-4';
const thumbnailButtonClasses =
  'relative h-20 w-24 flex-shrink-0 overflow-hidden rounded bg-muted ' +
  'disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';
const thumbnailImageClasses = 'h-full w-full object-cover';
// Inline self-hosted playback tile — same 96x80 footprint as the link-out
// thumbnail button so the row layout is stable across both videoUrl modes.
const selfHostedVideoClasses =
  'h-20 w-24 flex-shrink-0 overflow-hidden rounded bg-muted object-cover';
const playOverlayClasses =
  'absolute inset-0 flex items-center justify-center bg-foreground/40 transition-colors group-hover:bg-foreground/50';
const playGlyphClasses = 'h-8 w-8 text-background';
const titleClasses = 'flex-1 text-base font-semibold text-foreground';
const disclosureButtonClasses =
  'flex w-full items-center justify-between gap-2 text-left text-sm font-medium text-foreground ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';
const disclosureChevronClasses = 'h-4 w-4 flex-shrink-0 transition-transform';
const descriptionClasses =
  'mt-3 text-sm text-muted-foreground [&_a]:text-tab-active [&_a]:underline';

/** Decorative play triangle — the button's accessible name carries the meaning. */
function PlayGlyph(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={playGlyphClasses}>
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

export function LakiruutuVideoCard({
  video,
  t,
}: {
  readonly video: VideoListing;
  readonly t: (key: MessageKeys) => string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const descriptionId = useId();
  // Review finding: gate the EXTERNAL link-out on a safe http(s) scheme, not merely a
  // non-empty string. Covers BOTH the empty-string case (no playable URL, the
  // proto's `disabled={!videoUrl}`) AND an authored javascript:/data: URL, which
  // must never reach the `window.open` sink (read-side defense-in-depth).
  // Normalise once to the TRIMMED value (the client validator stores the trimmed
  // URL): a stored `'/media/x.mp4 '` must classify + render identically to
  // `'/media/x.mp4'`. `isSameOriginAssetPath` trims internally but `isVideoUrl`
  // does not — trimming once keeps the guard's two calls symmetric (and the
  // rendered `src` / `window.open` target clean).
  const videoUrl = video.videoUrl.trim();
  const hasVideo = isSafeHttpUrl(videoUrl);
  // Media re-point: a SELF-HOSTED same-origin video asset (`/media/x.mp4`) plays
  // INLINE from our origin. This is the only branch that does NOT use the
  // link-out button — a `<video controls>` is itself interactive and must not
  // nest inside a `<button>`.
  const selfHostedVideo = isSameOriginAssetPath(videoUrl) && isVideoUrl(videoUrl);

  return (
    <article className={cardClasses}>
      <div className={topRowClasses}>
        {selfHostedVideo ? (
          // SELF-HOSTED → inline playback from our own origin. Primary content:
          // NOT muted, NOT aria-hidden, accessible name from the entry title.
          // The thumbnail (always an image now) serves as the `poster`. The
          // snapshot read shape carries no caption track, so captions are out of
          // this slice; the entry title is the video's accessible name.
          // biome-ignore lint/a11y/useMediaCaption: authored member video; the snapshot read shape carries no caption track, and the entry title is the video's accessible name. Caption authoring is out of this slice.
          <video
            src={videoUrl}
            className={selfHostedVideoClasses}
            controls
            aria-label={video.title}
            poster={video.thumbnailUrl ?? undefined}
          />
        ) : (
          <button
            type="button"
            className={`group ${thumbnailButtonClasses}`}
            disabled={!hasVideo}
            // Per-card accessible name: the static play label plus the entry
            // title, so a screen-reader user navigating by button role hears
            // WHICH video each Play opens, not N identical "Toista video" buttons.
            aria-label={`${t('routes.lakiruutu.playNewTab')}: ${video.title}`}
            onClick={() => {
              // EXTERNAL link-out playback (R2 / research D9): open the external
              // video in a new tab with no opener handle and no referrer.
              window.open(videoUrl, '_blank', 'noopener,noreferrer');
            }}
          >
            {video.thumbnailUrl !== null ? (
              // Media re-point: the thumbnail field is IMAGE-only now, so the
              // poster is ALWAYS an `<img>` (no `<video>` poster branch).
              <img src={video.thumbnailUrl} alt="" className={thumbnailImageClasses} />
            ) : null}
            <span className={playOverlayClasses}>
              <PlayGlyph />
            </span>
          </button>
        )}
        <h3 className={titleClasses}>{video.title}</h3>
      </div>

      <button
        type="button"
        className={disclosureButtonClasses}
        aria-expanded={open}
        aria-controls={descriptionId}
        // Per-card accessible name: the visible "Katso lisää tietoja" plus the
        // entry title, so the N disclosure toggles are distinguishable by AT.
        aria-label={`${t('routes.lakiruutu.showMore')}: ${video.title}`}
        onClick={() => {
          setOpen((prev) => !prev);
        }}
      >
        <span>{t('routes.lakiruutu.showMore')}</span>
        <ChevronDownIcon className={`${disclosureChevronClasses} ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* The description stays mounted when collapsed (hidden attribute) so the
          authored HTML is always sanitised in place and the disclosure is a pure
          show/hide. `SanitizedHtml` strips script + event handlers (FR-049). */}
      <SanitizedHtml
        as="div"
        id={descriptionId}
        hidden={!open}
        className={descriptionClasses}
        html={video.description}
      />
    </article>
  );
}
